import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  Table,
  Chip,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Divider,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  InputLabel,
  useMediaQuery,
  useTheme,
  Stack,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import kidsClubService from '../api/services/kidsClubService';
import { getErrorMessage } from '../api/axiosConfig';
import WeeklyScheduleEditor from '../components/KidsClub/WeeklyScheduleEditor';
import type { KidsClubSettings, CapacityOverride } from '../types';

const KidsClubAdminPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [settings, setSettings] = useState<KidsClubSettings | null>(null);
  const [overrides, setOverrides] = useState<CapacityOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [openOverrideDialog, setOpenOverrideDialog] = useState(false);
  const [editingOverrideId, setEditingOverrideId] = useState<string | null>(null);
  const [overrideType, setOverrideType] = useState<'full-day' | 'time-specific'>('full-day');
  const [timeSlotType, setTimeSlotType] = useState<'morning' | 'afternoon' | 'evening' | 'custom'>('custom');
  const [overrideFilter, setOverrideFilter] = useState<'all' | 'open' | 'closed'>('all');

  const [overrideForm, setOverrideForm] = useState<Partial<CapacityOverride>>({
    date: new Date().toISOString().split('T')[0],
    max_capacity: 0,
    is_closed: false,
    notes: '',
    start_time: '18:00',
    end_time: '22:00',
    session: 'EVENING',
    required_staff_count: 2,
    special_event_name: '',
  });

  useEffect(() => {
    loadSettings();
    loadOverrides();
  }, []);

  useEffect(() => {
    if (success) {
      setTimeout(() => setSuccess(false), 3000);
    }
  }, [success]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await kidsClubService.getCapacitySettings();
      setSettings(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const loadOverrides = async () => {
    try {
      const startDate = new Date();
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 3); // Get 3 months ahead
      const data = await kidsClubService.getCapacityOverrides(
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0]
      );
      setOverrides(data);
    } catch (err) {
      console.error('Failed to load overrides:', err);
    }
  };

  const handleCreateOverride = async () => {
    if (!overrideForm.date) {
      setError('Please select a date');
      return;
    }

    if (!overrideForm.special_event_name || overrideForm.special_event_name.trim() === '') {
      setError('Special Event Name is required for reporting');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Prepare the override data
      const overrideData: Partial<CapacityOverride> = {
        date: overrideForm.date,
        max_capacity: overrideForm.max_capacity,
        is_closed: overrideForm.is_closed,
        notes: overrideForm.notes,
        special_event_name: overrideForm.special_event_name, // Include for all override types
      };

      // Add time-specific fields if override type is time-specific, or clear them if switching to full-day
      if (overrideType === 'time-specific') {
        overrideData.start_time = overrideForm.start_time;
        overrideData.end_time = overrideForm.end_time;
        overrideData.session = overrideForm.session;
        overrideData.required_staff_count = overrideForm.required_staff_count;
      } else if (editingOverrideId) {
        // When editing and switching to full-day, explicitly clear time-specific fields
        // Send empty strings so they're included in JSON payload (undefined gets dropped)
        // Session is handled automatically by backend when start_time is cleared
        overrideData.start_time = '';
        overrideData.end_time = '';
        overrideData.required_staff_count = undefined;
      }

      // Check if we're editing or creating
      if (editingOverrideId) {
        await kidsClubService.updateCapacityOverride(editingOverrideId, overrideData);
      } else {
        await kidsClubService.createCapacityOverride(overrideData);
      }

      setSuccess(true);
      setOpenOverrideDialog(false);
      resetOverrideForm();
      loadOverrides();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEditOverride = (override: CapacityOverride) => {
    setEditingOverrideId(override.id);

    // Helper function to format time from HH:MM:SS to HH:MM
    const formatTime = (timeStr?: string): string => {
      if (!timeStr) return '';
      // If time is in HH:MM:SS format, extract HH:MM
      const parts = timeStr.split(':');
      if (parts.length >= 2) {
        return `${parts[0]}:${parts[1]}`;
      }
      return timeStr;
    };

    // Populate form with existing data
    const formData: Partial<CapacityOverride> = {
      date: override.date,
      max_capacity: override.max_capacity,
      is_closed: override.is_closed,
      notes: override.notes || '',
      start_time: formatTime(override.start_time) || '18:00',
      end_time: formatTime(override.end_time) || '22:00',
      session: override.session || 'EVENING',
      required_staff_count: override.required_staff_count || 2,
      special_event_name: override.special_event_name || '',
    };
    setOverrideForm(formData);

    // Detect if it's a time-specific override
    if (override.start_time && override.end_time) {
      setOverrideType('time-specific');

      // Determine time slot type based on times (compare formatted versions)
      const formattedStart = formatTime(override.start_time);
      const formattedEnd = formatTime(override.end_time);

      if (formattedStart === '08:00' && formattedEnd === '12:30') {
        setTimeSlotType('morning');
      } else if (formattedStart === '12:30' && formattedEnd === '17:00') {
        setTimeSlotType('afternoon');
      } else if (formattedStart === '17:00' && formattedEnd === '21:00') {
        setTimeSlotType('evening');
      } else {
        setTimeSlotType('custom');
      }
    } else {
      setOverrideType('full-day');
    }

    setOpenOverrideDialog(true);
  };

  const handleDeleteOverride = async (overrideId: string) => {
    if (!window.confirm('Are you sure you want to delete this override?')) {
      return;
    }

    try {
      await kidsClubService.deleteCapacityOverride(overrideId);
      setSuccess(true);
      loadOverrides();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const resetOverrideForm = () => {
    setOverrideForm({
      date: new Date().toISOString().split('T')[0],
      max_capacity: 0,
      is_closed: false,
      notes: '',
      start_time: '18:00',
      end_time: '22:00',
      session: 'EVENING',
      required_staff_count: 2,
      special_event_name: '',
    });
    setOverrideType('full-day');
    setTimeSlotType('custom');
    setEditingOverrideId(null);
  };

  const handleTimeSlotTypeChange = (slotType: 'morning' | 'afternoon' | 'evening' | 'custom') => {
    setTimeSlotType(slotType);

    // Set default times based on slot type
    if (slotType === 'morning') {
      setOverrideForm({
        ...overrideForm,
        start_time: '08:00',
        end_time: '12:30',
        session: 'AM',
      });
    } else if (slotType === 'afternoon') {
      setOverrideForm({
        ...overrideForm,
        start_time: '12:30',
        end_time: '17:00',
        session: 'PM',
      });
    } else if (slotType === 'evening') {
      setOverrideForm({
        ...overrideForm,
        start_time: '17:00',
        end_time: '21:00',
        session: 'EVENING',
      });
    }
    // For 'custom', don't change the times - user will set them manually
  };

  const handleSaveWeeklySchedule = async (weeklySchedule: KidsClubSettings['weekly_schedule']) => {
    try {
      const updatedSettings = await kidsClubService.updateWeeklySchedule(weeklySchedule);
      // Update settings state directly with the returned data to ensure UI sync
      setSettings(updatedSettings);
    } catch (err) {
      console.error('KidsClubAdminPage: Error in handleSaveWeeklySchedule:', err);
      throw err;
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Kids Club Settings
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage capacity settings and date-specific overrides
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
            Settings saved successfully!
          </Alert>
        )}

        {/* Weekly Schedule Editor */}
        {settings && settings.weekly_schedule && (
          <WeeklyScheduleEditor
            weeklySchedule={settings.weekly_schedule}
            onSave={handleSaveWeeklySchedule}
            loading={loading}
          />
        )}

        <Divider sx={{ my: 3 }} />

        {/* Capacity Overrides */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">
                  Date-Specific Overrides
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Set custom capacity or close Kids Club for specific dates (holidays, events, etc.)
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setOpenOverrideDialog(true)}
              >
                Add Override
              </Button>
            </Box>

            {/* Filter Control */}
            {overrides.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Filter by Status</InputLabel>
                  <Select
                    value={overrideFilter}
                    label="Filter by Status"
                    onChange={(e) => setOverrideFilter(e.target.value as 'all' | 'open' | 'closed')}
                  >
                    <MenuItem value="all">All Overrides</MenuItem>
                    <MenuItem value="open">Open Only</MenuItem>
                    <MenuItem value="closed">Closed Only</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            {overrides.length === 0 ? (
              <Alert severity="info">
                No overrides set. Click "Add Override" to create one.
              </Alert>
            ) : (
              <>
                {/* Mobile Card View */}
                {isMobile ? (
                  <Stack spacing={2}>
                    {overrides
                      .filter((override) => {
                        if (overrideFilter === 'all') return true;
                        if (overrideFilter === 'open') return !override.is_closed;
                        if (overrideFilter === 'closed') return override.is_closed;
                        return true;
                      })
                      .length === 0 ? (
                        <Alert severity="info">
                          No {overrideFilter === 'all' ? '' : overrideFilter} overrides found
                        </Alert>
                      ) : (
                        overrides
                          .filter((override) => {
                            if (overrideFilter === 'all') return true;
                            if (overrideFilter === 'open') return !override.is_closed;
                            if (overrideFilter === 'closed') return override.is_closed;
                            return true;
                          })
                          .map((override) => (
                            <Card key={override.id} variant="outlined">
                              <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                      {new Date(override.date + 'T00:00:00').toLocaleDateString('en-US', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                      })}
                                    </Typography>
                                    {override.special_event_name && (
                                      <Chip
                                        label={override.special_event_name}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ mb: 1 }}
                                      />
                                    )}
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleEditOverride(override)}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteOverride(override.id)}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                </Box>

                                <Stack spacing={1}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                      Status:
                                    </Typography>
                                    {override.is_closed ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'error.main' }}>
                                        <EventBusyIcon fontSize="small" />
                                        <Typography variant="body2" fontWeight="medium">CLOSED</Typography>
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="success.main" fontWeight="medium">
                                        OPEN
                                      </Typography>
                                    )}
                                  </Box>

                                  {!override.is_closed && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Typography variant="body2" color="text.secondary">
                                        Custom Capacity:
                                      </Typography>
                                      <Typography variant="body2" fontWeight="medium">
                                        {override.max_capacity} children
                                      </Typography>
                                    </Box>
                                  )}

                                  {override.notes && (
                                    <Box>
                                      <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Notes:
                                      </Typography>
                                      <Typography variant="body2">
                                        {override.notes}
                                      </Typography>
                                    </Box>
                                  )}
                                </Stack>
                              </CardContent>
                            </Card>
                          ))
                      )}
                  </Stack>
                ) : (
                  /* Desktop Table View */
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Event Name</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Custom Capacity</TableCell>
                          <TableCell>Notes</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {overrides
                          .filter((override) => {
                            if (overrideFilter === 'all') return true;
                            if (overrideFilter === 'open') return !override.is_closed;
                            if (overrideFilter === 'closed') return override.is_closed;
                            return true;
                          })
                          .length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} align="center">
                                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                  No {overrideFilter === 'all' ? '' : overrideFilter} overrides found
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            overrides
                              .filter((override) => {
                                if (overrideFilter === 'all') return true;
                                if (overrideFilter === 'open') return !override.is_closed;
                                if (overrideFilter === 'closed') return override.is_closed;
                                return true;
                              })
                              .map((override) => (
                          <TableRow key={override.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {new Date(override.date + 'T00:00:00').toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {override.special_event_name ? (
                                <Chip
                                  label={override.special_event_name}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {override.is_closed ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'error.main' }}>
                                  <EventBusyIcon fontSize="small" />
                                  <Typography variant="body2">CLOSED</Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="success.main">
                                  OPEN
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {override.is_closed ? (
                                <Typography variant="body2" color="text.secondary">
                                  N/A
                                </Typography>
                              ) : (
                                <Typography variant="body2">
                                  {override.max_capacity} children
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                {override.notes || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Edit Override">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEditOverride(override)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Override">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteOverride(override.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        )))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Override Dialog */}
        <Dialog open={openOverrideDialog} onClose={() => { setOpenOverrideDialog(false); resetOverrideForm(); }} maxWidth="md" fullWidth>
          <DialogTitle>{editingOverrideId ? 'Edit Capacity Override' : 'Create Capacity Override'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {/* Date Selection */}
              <TextField
                label="Date"
                type="date"
                fullWidth
                required
                value={overrideForm.date}
                onChange={(e) => setOverrideForm({ ...overrideForm, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />

              {/* Override Type Selection */}
              <FormControl component="fieldset">
                <FormLabel component="legend">Override Type</FormLabel>
                <RadioGroup
                  row
                  value={overrideType}
                  onChange={(e) => setOverrideType(e.target.value as 'full-day' | 'time-specific')}
                >
                  <FormControlLabel value="full-day" control={<Radio />} label="Full Day" />
                  <FormControlLabel value="time-specific" control={<Radio />} label="Time-Specific" />
                </RadioGroup>
              </FormControl>

              {/* Special Event Name - Required for all override types */}
              <TextField
                label="Special Event Name"
                fullWidth
                required
                value={overrideForm.special_event_name}
                onChange={(e) => setOverrideForm({ ...overrideForm, special_event_name: e.target.value })}
                placeholder="e.g., Wine Tasting Night, Holiday Closure, Adult Party Event"
                helperText="Required for reporting and identification"
              />

              {/* Time-Specific Options */}
              {overrideType === 'time-specific' && (
                <>
                  <FormControl fullWidth>
                    <InputLabel>Time Slot</InputLabel>
                    <Select
                      value={timeSlotType}
                      label="Time Slot"
                      onChange={(e) => handleTimeSlotTypeChange(e.target.value as any)}
                    >
                      <MenuItem value="morning">Morning (8:00 AM - 12:30 PM)</MenuItem>
                      <MenuItem value="afternoon">Afternoon (12:30 PM - 5:00 PM)</MenuItem>
                      <MenuItem value="evening">Evening (5:00 PM - 9:00 PM)</MenuItem>
                      <MenuItem value="custom">Custom Time Range</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Custom Time Inputs */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Start Time"
                      type="time"
                      fullWidth
                      required
                      value={overrideForm.start_time}
                      onChange={(e) => setOverrideForm({ ...overrideForm, start_time: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      disabled={timeSlotType !== 'custom'}
                    />
                    <TextField
                      label="End Time"
                      type="time"
                      fullWidth
                      required
                      value={overrideForm.end_time}
                      onChange={(e) => setOverrideForm({ ...overrideForm, end_time: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      disabled={timeSlotType !== 'custom'}
                    />
                  </Box>

                  <TextField
                    label="Required Staff Count"
                    type="number"
                    fullWidth
                    value={overrideForm.required_staff_count}
                    onChange={(e) =>
                      setOverrideForm({ ...overrideForm, required_staff_count: parseInt(e.target.value) || 2 })
                    }
                    onFocus={(e) => e.target.select()}
                    inputProps={{ min: 1, max: 10 }}
                    helperText="Minimum staff members required for this time slot"
                  />
                </>
              )}

              {/* Closed Toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={overrideForm.is_closed || false}
                    onChange={(e) =>
                      setOverrideForm({
                        ...overrideForm,
                        is_closed: e.target.checked,
                        max_capacity: e.target.checked ? 0 : overrideForm.max_capacity,
                      })
                    }
                  />
                }
                label={
                  overrideType === 'time-specific'
                    ? 'Close Kids Club for this time slot'
                    : 'Close Kids Club for this date'
                }
              />

              {/* Capacity */}
              {!overrideForm.is_closed && (
                <TextField
                  label="Custom Capacity"
                  type="number"
                  fullWidth
                  required
                  value={overrideForm.max_capacity}
                  onChange={(e) =>
                    setOverrideForm({ ...overrideForm, max_capacity: parseInt(e.target.value) || 0 })
                  }
                  onFocus={(e) => e.target.select()}
                  helperText={
                    overrideType === 'time-specific'
                      ? 'Maximum number of children for this time slot'
                      : 'Maximum number of children for this specific date'
                  }
                  inputProps={{ min: 1, max: 50 }}
                />
              )}

              {/* Notes */}
              <TextField
                label="Notes"
                multiline
                rows={3}
                fullWidth
                value={overrideForm.notes}
                onChange={(e) => setOverrideForm({ ...overrideForm, notes: e.target.value })}
                placeholder="e.g., Holiday closure, Special event, Reduced staff..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setOpenOverrideDialog(false); resetOverrideForm(); }}>Cancel</Button>
            <Button
              onClick={handleCreateOverride}
              variant="contained"
              disabled={loading || !overrideForm.date}
              startIcon={loading ? <CircularProgress size={20} /> : (editingOverrideId ? <EditIcon /> : <AddIcon />)}
            >
              {loading ? (editingOverrideId ? 'Updating...' : 'Creating...') : (editingOverrideId ? 'Update Override' : 'Create Override')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default KidsClubAdminPage;
